import React, { useState } from 'react'
import { Camera, Upload, Lightbulb } from 'lucide-react'
import { ExpandIcon, Rotate3D } from "lucide-react"
import { DimensionsIcon } from '../lib/svgs/DimensionsIcon.js'
import { ArIcon } from '../lib/svgs/ArIcon.js'
import { toggleDimensions, toggleAnimation, toggleFullscreen, getAnimationButtonText } from '../utils/configurator-utils.js'
import { useOV25UI } from '../contexts/ov25-ui-context.js'
import { cn } from '../lib/utils.js'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover.js'
import { toast } from 'sonner'
import Snap2Controls from './Snap2Controls.js'
import { VariantsCloseButton } from './VariantSelectMenu/VariantsCloseButton.js'

interface ConfiguratorViewControlsProps {
  // All props now come from context, so no props needed
}

const ConfiguratorViewControls: React.FC<ConfiguratorViewControlsProps> = () => {

  const {
    isVariantsOpen,
    setIsVariantsOpen,
    isMobile,
    canAnimate,
    animationState,
    currentProduct,
    availableCameras,
    selectCamera,
    availableLights,
    selectLightGroup,
    isSnap2Mode,
    configuratorDisplayModeMobile,
    controlsHidden,
    shareDialogTrigger,
    setShareDialogTrigger,
    configuratorState,
    hasConfigureButton,
    toggleAR,
    hideAr,
    uniqueId,
    cssString,
    getString,
  } = useOV25UI();

  // Local state for dimensions
  const [canSeeDimensions, setCanSeeDimensions] = useState(false);
  // Local state for camera popover
  const [isCameraPopoverOpen, setIsCameraPopoverOpen] = useState(false);
  const cameraButtonRef = React.useRef<HTMLButtonElement>(null);
  // Local state for lights popover
  const [isLightPopoverOpen, setIsLightPopoverOpen] = useState(false);
  const lightButtonRef = React.useRef<HTMLButtonElement>(null);

  // Click outside handler to close popover
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isCameraPopoverOpen && !target.closest('[data-popover]')) {
        setIsCameraPopoverOpen(false);
      }
    };

    if (isCameraPopoverOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCameraPopoverOpen]);

  // Click outside handler for lights popover
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isLightPopoverOpen && !target.closest('[data-popover]')) {
        setIsLightPopoverOpen(false);
      }
    };

    if (isLightPopoverOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLightPopoverOpen]);

  // Calculate showDimensionsToggle from currentProduct
  const showDimensionsToggle = !isSnap2Mode && !!((currentProduct as any)?.dimensionX &&
    (currentProduct as any)?.dimensionY &&
    (currentProduct as any)?.dimensionZ);

  const handleToggleDimensions = () => {
    toggleDimensions(canSeeDimensions, setCanSeeDimensions, uniqueId, cssString);
  }

  const handleAnimationButtonText = () => {
    return getAnimationButtonText(canAnimate, animationState);
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(getString('shareLinkCopied', undefined, 'Share link copied to clipboard!'), { duration: 3000 });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(getString('shareLinkCopied', undefined, 'Share link copied to clipboard!'));
    }
  };

  const handleShare = async () => {
    const currentUrl = window.location.href;
    const productName = currentProduct?.name || 'product';
    const shareTitle = getString(
      'shareTitle',
      { PRODUCT_NAME: productName },
      `Share this ${productName} configuration with your friends!`
    );
    const shareText = getString(
      'shareText',
      { PRODUCT_NAME: productName },
      `I just designed this ${productName} — take a look!`
    );
    // Check if Web Share API is available (mobile devices)
    if (navigator.share && isMobile) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: currentUrl,
        });
      } catch (error) {
        // User cancelled or share failed — fallback
        await copyToClipboard(currentUrl);
      }
    } else {
      // Web Share API not available or desktop — fallback
      await copyToClipboard(currentUrl);
    }

  }

  return (
    <>
      {/* Snap2 Controls - shown only in snap2 mode */}
      {isSnap2Mode && <Snap2Controls />}

      {/* <div className={cn(
        "ov:absolute ov:w-full ov:pointer-events-none ov:h-full ov:inset-0 ov:gap-2 ov:p-4 ov:flex ov:justify-end ov:items-end ov:z-[101]",
        "ov:text-[var(--ov25-configurator-view-controls-text-color)]"
      )}>
        {canAnimate && (
          <button onClick={toggleAnimation} className={cn(
            'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
            'ov:rounded-[var(--ov25-configurator-view-controls-border-radius)]',
            'ov:shadow-sm ov:bg-[var(--ov25-overlay-button-color)]',
          )}>
              <Rotate3D strokeWidth={1} className="ov:w-[19px] ov:h-[19px] p-1"/>
              {!isMobile && (
              <p className="ov25-controls-text ov25-360-label ov:text-sm ov:font-light">{handleAnimationButtonText()}</p>
              )}
          </button>
        )}
    </div> */}

      <div className={cn(
        "ov:pointer-events-none ov:absolute ov:w-full ov:h-full ov:inset-0 ov:gap-2 ov:p-4 ov:flex ov:justify-end ov:items-end ov:z-101",
        "ov:text-(--ov25-configurator-view-controls-text-color)",
        "ov:transition-all ov:duration-200",
        shareDialogTrigger !== 'none' && "!ov:opacity-0 !ov:pointer-events-none"
      )}>
        <div className="ov:flex ov:flex-row ov:gap-2 ov:items-end">
          {!controlsHidden && !isSnap2Mode && <button id="ov25-share-button" onClick={handleShare} className={cn(
            'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
            'ov:border ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
            'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
          )}>
            <Upload strokeWidth={1} className="ov:w-[19px] ov:h-[19px] p-1" color="var(--ov25-configurator-view-controls-text-color)"/>
            {!isMobile && (
              <p className="ov25-controls-text ov:text-sm ov:text-(--ov25-text-color)">{getString('controlsShareLabel', undefined, 'Share')}</p>
            )}
          </button>}
          {canAnimate && (
            <button id="ov25-animation-toggle-button" onClick={() => toggleAnimation(uniqueId)} className={cn(
              'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
              'ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
              'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
            )}>
              <Rotate3D strokeWidth={1} className="ov:w-[19px] ov:h-[19px] p-1" color="var(--ov25-configurator-view-controls-text-color)" />
              {!isMobile && (
                <p className="ov25-controls-text ov25-360-label ov:text-sm ov:font-light">{handleAnimationButtonText()}</p>
              )}
            </button>
          )}

          {showDimensionsToggle && (
            <button id="ov25-desktop-dimensions-toggle-button" onClick={handleToggleDimensions} className={cn(
              'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
              'ov:border ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
              'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
            )}>
              <DimensionsIcon className="ov:w-[19px] ov:h-[19px] p-1" color="var(--ov25-configurator-view-controls-text-color)" />
              {!isMobile && (
                <p className="ov25-controls-text ov:text-sm ov:text-(--ov25-text-color)">{getString('controlsDimensionsLabel', undefined, 'Dimensions')}</p>
              )}
            </button>
          )}

          {availableCameras.length > 1 && !isSnap2Mode && (
            <div data-popover>
              <Popover open={isCameraPopoverOpen} onOpenChange={setIsCameraPopoverOpen}>
                <PopoverTrigger onClick={() => setIsCameraPopoverOpen(!isCameraPopoverOpen)}>
                  <button
                    ref={cameraButtonRef}
                    id="ov25-camera-toggle-button"
                    className={cn(
                      'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
                      'ov:border ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
                      'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
                    )}
                  >
                    <Camera strokeWidth={1} className="ov:w-[19px] ov:h-[19px] p-1" />
                    {!isMobile && (
                      <p className="ov25-controls-text ov:text-sm ov:text-(--ov25-text-color)">{getString('controlsCameraLabel', undefined, 'Camera')}</p>
                    )}
                  </button>
                </PopoverTrigger>
                {isCameraPopoverOpen && (
                  <PopoverContent triggerRef={cameraButtonRef}>
                    <div className="ov:flex ov:flex-col ov:gap-1">
                      {availableCameras.map((camera, index) => (
                        <button
                          key={camera.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectCamera(camera.id);
                            setIsCameraPopoverOpen(false);
                          }}
                          className={cn(
                            'ov:px-3 ov:py-2 ov:text-sm ov:rounded ov:cursor-pointer ov:hover:bg-gray-100',
                            'ov:transition-colors ov:duration-200 ov:text-left ov:w-full ov:bg-transparent ov:border-none'
                          )}
                        >
                          {getString(
                            'cameraName',
                            {
                              CAMERA_NAME:
                                camera.displayName && camera.displayName.trim() !== ''
                                  ? camera.displayName
                                  : getString('controlsCameraFallback', { CAMERA_INDEX: index + 1 }, `Camera ${index + 1}`),
                              CAMERA_INDEX: index + 1,
                            },
                            camera.displayName && camera.displayName.trim() !== ''
                              ? camera.displayName
                              : getString('controlsCameraFallback', { CAMERA_INDEX: index + 1 }, `Camera ${index + 1}`)
                          )}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                )}
              </Popover>
            </div>
          )}

          {availableLights.length > 1 && (
            <div data-popover>
              <Popover open={isLightPopoverOpen} onOpenChange={setIsLightPopoverOpen}>
                <PopoverTrigger onClick={() => setIsLightPopoverOpen(!isLightPopoverOpen)}>
                  <button
                    ref={lightButtonRef}
                    id="ov25-light-toggle-button"
                    className={cn(
                      'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
                      'ov:border ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
                      'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
                    )}
                  >
                    <Lightbulb strokeWidth={1} className="ov:w-[19px] ov:h-[19px] p-1" color="var(--ov25-configurator-view-controls-text-color)" />
                    {!isMobile && (
                      <p className="ov25-controls-text ov:text-sm ov:text-(--ov25-text-color)">{getString('controlsLightsLabel', undefined, 'Lights')}</p>
                    )}
                  </button>
                </PopoverTrigger>
                {isLightPopoverOpen && (
                  <PopoverContent triggerRef={lightButtonRef}>
                    <div className="ov:flex ov:flex-col ov:gap-1">
                      {availableLights.map((group, index) => (
                        <button
                          key={group.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            selectLightGroup(group.id);
                            setIsLightPopoverOpen(false);
                          }}
                          className={cn(
                            'ov:px-3 ov:py-2 ov:text-sm ov:rounded ov:cursor-pointer ov:hover:bg-gray-100',
                            'ov:transition-colors ov:duration-200 ov:text-left ov:w-full ov:bg-transparent ov:border-none'
                          )}
                        >
                          {group.displayName && group.displayName.trim() !== '' ? group.displayName : getString('controlsLightFallback', { GROUP_INDEX: index + 1 }, `Group ${index + 1}`)}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                )}
              </Popover>
            </div>
          )}

          {!hideAr && !isSnap2Mode && (
            <button id="ov25-ar-toggle-button" onClick={toggleAR} className={cn(
              'ov:cursor-pointer ov:pointer-events-auto ov:flex ov:gap-2.5 ov:p-2 ov:items-center ov:justify-center',
              'ov:border ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
              'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
            )}>
              <ArIcon className="ov:w-[19px] ov:h-[19px] p-1" color="var(--ov25-configurator-view-controls-text-color)" />
              {!isMobile && (
                <p className="ov25-controls-text ov:text-sm ov:text-(--ov25-text-color)">{getString('controlsArLabel', undefined, 'View in your room')}</p>
              )}
            </button>
          )}

        </div>
      </div>
      {isVariantsOpen &&
        isMobile &&
        !(isSnap2Mode && configuratorDisplayModeMobile === 'inline') && (
        <div className={cn(
          "ov:absolute ov:w-full ov:pointer-events-none ov:h-full ov:inset-0 ov:z-101",
          "ov:transition-opacity ov:duration-200",
          shareDialogTrigger !== 'none' && "ov:opacity-0"
        )}>
          <VariantsCloseButton />
        </div>
      )}
      {!isVariantsOpen && !isMobile && !controlsHidden && !isSnap2Mode && !hasConfigureButton && (
        <div className="ov:absolute ov:ov25-controls-hidden ov:size-full ov:md:flex ov:pointer-events-none ov:inset-0 ov:p-4 ov:justify-end ov:items-start ov:z-101">
          <button id="ov25-desktop-fullscreen-button" className={cn(
            'ov:cursor-pointer ov:aspect-square ov:p-2 ov:pointer-events-auto ov:flex ov:gap-2.5 ov:ml-auto ov:items-center ov:justify-center',
            'ov:border ov:rounded-(--ov25-configurator-view-controls-border-radius) ov:border-(--ov25-configurator-view-controls-border-color)',
            'ov:shadow-sm ov:bg-(--ov25-overlay-button-color)',
          )}
            onClick={() => toggleFullscreen(uniqueId)}>
            <ExpandIcon strokeWidth={1} className="ov:w-[19px] ov:h-[19px] p-1" color="var(--ov25-configurator-view-controls-text-color)" />
          </button>
        </div>
      )}
    </>
  )
}

export default ConfiguratorViewControls 